/* ==========================================================================
   QUICK VIEW — full script with GLOBAL adjustable delayed force-close
   Fixes: previous quick view stays logically open (overlay/::after catches clicks)
   Tune timing: QUICK_VIEW_CLOSE_DELAY_MS
   ========================================================================== */

(function () {
  /* ---------- GLOBAL TIMING KNOB (adjust this) ---------- */
  const QUICK_VIEW_CLOSE_DELAY_MS = 900; // e.g. 120–350, set 0 for immediate

  /* ---------- Helpers ---------- */

  function schedule(fn, delayMs) {
    return window.setTimeout(fn, Math.max(0, Number(delayMs) || 0));
  }

  function reinjectScripts(containerEl) {
    containerEl.querySelectorAll('script').forEach(oldScriptTag => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach(attribute => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }

  /* ---------- <quick-view-drawer> ---------- */

  class QuickViewDrawer extends MenuDrawer {
    constructor() {
      super();

      this.setClasses({
        open: 'quick-view--open',
        opening: 'quick-view--opening',
        closing: 'quick-view--closing'
      });

      this._closeTimer = null;

      // Theme lifecycle close
      this.addEventListener('close', () => {
        const drawerContent = this.querySelector('.quick-view__content');
        if (drawerContent) {
          drawerContent.innerHTML = '';
          drawerContent.classList.remove('hide-cover');
        }
        document.dispatchEvent(new CustomEvent('quickview:close'));
      });
    }

    cancelPendingClose() {
      if (this._closeTimer) {
        clearTimeout(this._closeTimer);
        this._closeTimer = null;
      }
    }

    /**
     * Truly close so the overlay/backdrop/::after no longer captures clicks.
     * Uses global adjustable delay by default.
     */
    forceClose({ silent = false, delayMs = QUICK_VIEW_CLOSE_DELAY_MS } = {}) {
      this.cancelPendingClose();

      const doClose = () => {
        // Close underlying <details> (common in Shopify drawer patterns)
        const details = this.closest('details') || this.querySelector('details');
        if (details) details.removeAttribute('open');

        // Remove state classes (in case something got stuck)
        this.classList.remove(
          'quick-view--open',
          'quick-view--opening',
          'quick-view--closing'
        );

        // Clear DOM to prevent lingering click layers / scripts
        const drawerContent = this.querySelector('.quick-view__content');
        if (drawerContent) {
          drawerContent.innerHTML = '';
          drawerContent.classList.remove('hide-cover');
        }

        // Keep theme listeners happy unless silent
        if (!silent) {
          this.dispatchEvent(new CustomEvent('close', { bubbles: true }));
        }

        this._closeTimer = null;
      };

      this._closeTimer = schedule(doClose, delayMs);
    }

    /** Close all quick views, optionally leaving one open. */
    static closeAll(exceptDrawer = null, opts = {}) {
      document.querySelectorAll('quick-view-drawer').forEach(d => {
        if (d !== exceptDrawer && typeof d.forceClose === 'function') {
          d.forceClose({ silent: true, ...opts });
        }
      });
      document.dispatchEvent(new CustomEvent('quickview:closeall'));
    }

    /**
     * Opens the quick view drawer and sets the product URL.
     * Also closes any other open-but-hidden quick views first (delayed).
     */
    openWith(productUrl) {
      QuickViewDrawer.closeAll(this, { delayMs: QUICK_VIEW_CLOSE_DELAY_MS });

      // If THIS drawer had a pending delayed close from earlier, cancel it.
      this.cancelPendingClose();

      const quickViewEl = this.querySelector('quick-view');
      if (quickViewEl && productUrl) {
        quickViewEl.setAttribute('data-product-url', productUrl);
      }

      const summary = this.querySelector('summary');
      if (summary) summary.click();

      document.dispatchEvent(
        new CustomEvent('quickview:open', { detail: { productUrl } })
      );
    }
  }

  customElements.define('quick-view-drawer', QuickViewDrawer);

  /* ---------- <quick-view-button> ---------- */

  class QuickViewButton extends MenuDrawer {
    constructor() {
      super();

      this.addEventListener('click', () => {
        const wrapper = this.closest('.card-wrapper');
        const drawer = wrapper?.querySelector('quick-view-drawer');
        const productUrl = this.dataset.productUrl;

        if (drawer) {
          drawer.openWith(productUrl);
        } else if (productUrl) {
          // Fallback if no drawer in this card
          window.location = productUrl;
        }
      });
    }
  }

  customElements.define('quick-view-button', QuickViewButton);

  /* ---------- <quick-view> ---------- */

  class QuickView extends HTMLElement {
    constructor() {
      super();
      this._io = null;
    }

    connectedCallback() {
      if (this._io) this._io.disconnect();
      this._io = new IntersectionObserver(this.handleIntersection.bind(this));
      this._io.observe(this);
    }

    disconnectedCallback() {
      if (this._io) this._io.disconnect();
      this._io = null;
    }

    handleIntersection(entries) {
      if (!entries[0].isIntersecting) return;

      const selector = '.quick-view__content';
      const drawerContent = this.querySelector(selector);
      const rawUrl = this.dataset.productUrl || '';
      if (!rawUrl || !drawerContent) return;

      const productUrl = rawUrl.split('?')[0];
      const sectionUrl = `${productUrl}?view=modal`;

      fetch(sectionUrl)
        .then(response => response.text())
        .then(responseText => {
          setTimeout(() => {
            const responseHTML = new DOMParser().parseFromString(
              responseText,
              'text/html'
            );
            const productElement = responseHTML.querySelector(selector);
            if (productElement) {
              this.setInnerHTML(drawerContent, productElement.innerHTML);
            }

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
          }, 100);

          setTimeout(() => {
            drawerContent.classList.add('hide-cover');

            document.dispatchEvent(
              new CustomEvent('quickview:loaded', {
                detail: { productUrl: rawUrl }
              })
            );
          }, 200);
        })
        .catch(e => console.error(e));
    }

    setInnerHTML(element, html) {
      element.innerHTML = html;
      reinjectScripts(element);
    }
  }

  customElements.define('quick-view', QuickView);

  /* -------- Global delegation: make product image wrappers open Quick View --------
     Add data-quick-view-url and role="button" to your image wrapper element:
     <div class="media ..." data-quick-view-url="{{ card_product.url | within: card_collection }}" role="button" tabindex="0"></div>
  ------------------------------------------------------------------------------- */

  function openFromTrigger(triggerEl) {
    const url = triggerEl.getAttribute('data-quick-view-url');
    const card = triggerEl.closest('.card-wrapper');
    const drawer = card?.querySelector('quick-view-drawer');

    if (drawer && url) {
      drawer.openWith(url);
    } else if (url) {
      // Fallback if no drawer exists
      window.location = url;
    }
  }

  // Mouse/touch
  document.addEventListener(
    'click',
    function (e) {
      const trigger = e.target.closest(
        '.card-wrapper .media[role="button"][data-quick-view-url]'
      );
      if (!trigger) return;
      e.preventDefault();
      openFromTrigger(trigger);
    },
    { passive: false }
  );

  // Keyboard (Enter/Space)
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const trigger = e.target.closest(
      '.card-wrapper .media[role="button"][data-quick-view-url]'
    );
    if (!trigger) return;
    e.preventDefault();
    openFromTrigger(trigger);
  });

  /* ---------- Close quick view when add-to-cart happens (DELAYED) ---------- */

  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;

      const action = (form.getAttribute('action') || '').toLowerCase();
      if (!action.includes('/cart/add')) return;

      // If add-to-cart originates inside quick view, close all drawers (delayed)
      if (form.closest('quick-view') || form.closest('quick-view-drawer')) {
        QuickViewDrawer.closeAll(null, { delayMs: QUICK_VIEW_CLOSE_DELAY_MS });
      }
    },
    true
  );

  // Optional theme event hooks (safe no-op if theme doesn't emit these)
  ['cart:open', 'cart:toggle', 'drawer:open', 'theme:cart:open'].forEach(evt => {
    document.addEventListener(evt, () => {
      QuickViewDrawer.closeAll(null, { delayMs: QUICK_VIEW_CLOSE_DELAY_MS });
    });
  });

  /* ---------- Extra safety: when any quick view closes, remove stray <details open> ---------- */

  document.addEventListener('quickview:close', () => {
    document.querySelectorAll('quick-view-drawer details[open]').forEach(d => {
      d.removeAttribute('open');
    });
  });
})();